import React, { useEffect, useState } from "react";
import { 
  Button, 
  Table, 
  Space, 
  Modal, 
  message, 
  Card, 
  Input, 
  Select, 
  DatePicker, 
  Tag,
  Row, 
  Col,
  Typography,
  Badge
} from "antd";
import { 
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined,
  SyncOutlined 
} from "@ant-design/icons";
import moment from "moment";
import CreateFirmModal from "../components/CreateFirmModal";
import EditFirmModal from "../components/EditFirmModal";
import { toastError, toastSuccess } from "../../../utils/toast";
import { fetchFirms as apiFetchFirms, deleteFirmById as apiDeleteFirmById } from "../../../api/firmApi";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

export default function FirmPage() {
  const [firms, setFirms] = useState([]);
  const [filteredFirms, setFilteredFirms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFirm, setEditingFirm] = useState(null);
  const [filterParams, setFilterParams] = useState({
    search: '',
    status: '',
    dateRange: []
  });

  const statusOptions = ['Active', 'Inactive', 'Pending', 'Verified'];
  const statusColors = {
    Active: 'green',
    Inactive: 'red',
    Pending: 'orange',
    Verified: 'blue'
  };

  const fetchFirms = async () => {
    setLoading(true);
    try {
      const data = await apiFetchFirms();
      setFirms(data);
      setFilteredFirms(data);
    } catch (error) {
      message.error("Failed to fetch firms");
    } finally {
      setLoading(false);
    }
  };

  const deleteFirm = async (id) => {
    Modal.confirm({
      title: "Confirm Firm Deletion",
      content: "This action will permanently delete the firm and all associated data. Continue?",
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiDeleteFirmById(id);
          toastSuccess("Firm deleted successfully");
          fetchFirms();
        } catch (error) {
          console.error("Delete error:", error.response || error.message);
          toastError(error.response?.data?.message || "Failed to delete firm");
        }
      },
    });
  };

  const handleFilterChange = (name, value) => {
    setFilterParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    let result = [...firms];

    // Search filter
    if (filterParams.search) {
      const searchTerm = filterParams.search.toLowerCase();
      result = result.filter(firm => 
        firm.name.toLowerCase().includes(searchTerm) ||
        firm.email.toLowerCase().includes(searchTerm) ||
        (firm.contact_number && firm.contact_number.toLowerCase().includes(searchTerm))
      );
    }

    // Status filter
    if (filterParams.status) {
      result = result.filter(firm => firm.status === filterParams.status);
    }

    // Date range filter
    if (filterParams.dateRange && filterParams.dateRange.length === 2) {
      const [start, end] = filterParams.dateRange;
      result = result.filter(firm => {
        const createdAt = moment(firm.created_at);
        return createdAt.isBetween(start, end, 'day', '[]');
      });
    }

    setFilteredFirms(result);
  };

  const resetFilters = () => {
    setFilterParams({
      search: '',
      status: '',
      dateRange: []
    });
    setFilteredFirms(firms);
  };

  useEffect(() => {
    fetchFirms();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filterParams, firms]);

  const columns = [
    { 
      title: "Firm Name", 
      dataIndex: "name", 
      key: "name",
      render: (text) => <Text strong>{text}</Text>,
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    { 
      title: "Email", 
      dataIndex: "email", 
      key: "email",
      render: (email) => <Text type="secondary">{email}</Text>
    },
    { 
      title: "Contact Number", 
      dataIndex: "contact_number", 
      key: "contact_number",
      render: (number) => number || <Text type="secondary">N/A</Text>
    },
    { 
          title: "Status", 
          dataIndex: "status", 
          key: "status",
          render: (status) => (
            <Tag 
              color={
                status === 'Active' ? 'green' : 
                status === 'Inactive' ? 'red' : 'orange'
              }
            >
              {status}
            </Tag>
          ),
          filters: statusOptions.map(status => ({ text: status, value: status })),
          onFilter: (value, record) => record.status === value,
        },
   
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => moment(date).format("LLL"),
      sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix(),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EditOutlined />}
            type="text"
            size="small"
            onClick={() => setEditingFirm(record)}
            tooltip="Edit firm"
          />
          <Button
            icon={<DeleteOutlined />}
            type="text"
            danger
            size="small"
            onClick={() => deleteFirm(record.user_id)}
            tooltip="Delete firm"
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px' }}>
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4} style={{ margin: 0 }}>Corporate Firms Management</Title>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => setShowCreateModal(true)}
            >
              Add New Firm
            </Button>
          </div>
        }
        bordered={false}
        style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)' }}
      >
        {/* Filter Section */}
        <Card 
          style={{ marginBottom: 24 }} 
          bordered={false}
          bodyStyle={{ padding: 16 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input
                placeholder="Search firms..."
                prefix={<SearchOutlined />}
                value={filterParams.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                allowClear
              />
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filter by status"
                style={{ width: '100%' }}
                value={filterParams.status || undefined}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
              >
                {statusOptions.map(status => (
                  <Option key={status} value={status}>
                    <Badge color={statusColors[status] || 'gray'} text={status} />
                  </Option>
                ))}
              </Select>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <RangePicker
                style={{ width: '100%' }}
                value={filterParams.dateRange}
                onChange={(dates) => handleFilterChange('dateRange', dates)}
                placeholder={['Start Date', 'End Date']}
              />
            </Col>
            
            <Col xs={24} style={{ textAlign: 'right' }}>
              <Space>
                <Button 
                  icon={<FilterOutlined />} 
                  onClick={applyFilters}
                  type="primary"
                  ghost
                >
                  Apply Filters
                </Button>
                <Button 
                  icon={<SyncOutlined />} 
                  onClick={resetFilters}
                >
                  Reset
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Table
          rowKey="user_id"
          columns={columns}
          dataSource={filteredFirms}
          loading={loading}
          bordered
          scroll={{ x: true }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} firms`
          }}
        />
      </Card>

      <CreateFirmModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          fetchFirms();
          setShowCreateModal(false);
        }}
      />

      <EditFirmModal
        open={!!editingFirm}
        onClose={() => setEditingFirm(null)}
        firm={editingFirm}
        onSuccess={() => {
          fetchFirms();
          setEditingFirm(null);
        }}
      />
    </div>
  );
}